import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Copy,
  Check,
  ExternalLink,
  Users,
  Globe,
  Sparkles,
  TrendingUp,
  ShieldCheck,
  Rocket,
  Wallet,
  ShoppingBag,
  ArrowRight,
  Twitter,
  Send as TelegramIcon,
  MessageCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTRACT = "43F8BpSWpuDkDZojbNWvBYXe286CGEhjQGCNwN4348dn";
const COIN_IMG =
  "https://customer-assets.emergentagent.com/job_no-netlify-deploy/artifacts/e00xrmxm_file_0000000042788230b8312677e9e31287.png";

const NAV = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Tokenomics", href: "#tokenomics" },
  { label: "How to Buy", href: "#how-to-buy" },
  { label: "Roadmap", href: "#roadmap" },
  { label: "Community", href: "#community" },
];

const EXCHANGES = [
  {
    name: "Solscan",
    tag: "Explorer",
    url: `https://solscan.io/token/${CONTRACT}`,
    testid: "exchange-solscan",
  },
  {
    name: "DexScreener",
    tag: "Live Chart",
    url: `https://dexscreener.com/solana/${CONTRACT}`,
    testid: "exchange-dexscreener",
  },
  {
    name: "Jupiter",
    tag: "Swap",
    url: `https://jup.ag/swap/SOL-${CONTRACT}`,
    testid: "exchange-jupiter",
  },
  {
    name: "Coinbase",
    tag: "Wallet",
    url: "https://www.coinbase.com/wallet",
    testid: "exchange-coinbase",
  },
  {
    name: "FOMO",
    tag: "Discover",
    url: "https://fomo.co",
    testid: "exchange-fomo",
  },
];

export default function LandingPage() {
  const [copied, setCopied] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", wallet: "" });
  const [merchEmail, setMerchEmail] = useState("");
  const [contactForm, setContactForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState({ join: false, merch: false, contact: false });

  useEffect(() => {
    axios
      .get(`${API}/community/count`)
      .then((r) => setMemberCount(r.data.count || 0))
      .catch(() => {});
  }, []);

  const copyAddress = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(CONTRACT);
      } else {
        const ta = document.createElement("textarea");
        ta.value = CONTRACT;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success("Contract address copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(true);
      toast.message("Address selected — press Ctrl/Cmd+C to copy");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please enter your name and email");
      return;
    }
    setLoading((l) => ({ ...l, join: true }));
    try {
      await axios.post(`${API}/community/register`, form);
      toast.success("Welcome to the DORYIUM community!");
      setForm({ name: "", email: "", wallet: "" });
      setMemberCount((c) => c + 1);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Registration failed";
      toast.error(typeof msg === "string" ? msg : "Registration failed");
    } finally {
      setLoading((l) => ({ ...l, join: false }));
    }
  };

  const handleMerch = async (e) => {
    e.preventDefault();
    if (!merchEmail) return toast.error("Enter your email");
    setLoading((l) => ({ ...l, merch: true }));
    try {
      await axios.post(`${API}/merch/notify`, { email: merchEmail });
      toast.success("You'll be first to know when merch drops");
      setMerchEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed";
      toast.error(typeof msg === "string" ? msg : "Failed");
    } finally {
      setLoading((l) => ({ ...l, merch: false }));
    }
  };

  const handleContact = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      return toast.error("Fill all fields");
    }
    setLoading((l) => ({ ...l, contact: true }));
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Message received. We'll be in touch.");
      setContactForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Failed to send");
    } finally {
      setLoading((l) => ({ ...l, contact: false }));
    }
  };

  const shortAddr = `${CONTRACT.slice(0, 6)}...${CONTRACT.slice(-6)}`;

  return (
    <div className="relative min-h-screen text-white font-body">
      {/* NAV */}
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#05070d]/70 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <a href="#home" className="flex items-center gap-3" data-testid="nav-logo">
            <img src={COIN_IMG} alt="DORYIUM" className="w-9 h-9 rounded-full ring-2 ring-[#f5c65a]/60" />
            <span className="font-display text-xl gold-text tracking-widest">DORYIUM</span>
          </a>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.label}
                href={n.href}
                className="text-sm text-white/70 hover:text-[#ffd977] transition-colors font-heading uppercase tracking-wider"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {n.label}
              </a>
            ))}
          </nav>
          <a href="#how-to-buy" data-testid="nav-buy-btn">
            <button className="btn-outline-gold rounded-full px-5 py-2 text-sm font-heading uppercase tracking-widest">
              Buy DORY
            </button>
          </a>
        </div>
      </header>

      {/* HERO */}
      <section id="home" className="relative">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-14 lg:pt-20 pb-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow mb-6">People • Technology • Opportunity</div>
            <h1 className="font-display font-black gold-text leading-[0.95] text-6xl sm:text-7xl lg:text-8xl">
              DORYIUM
            </h1>
            <h2 className="mt-4 font-display font-bold text-white text-2xl sm:text-3xl lg:text-4xl uppercase tracking-wider">
              A Brighter Tomorrow<br />Together
            </h2>
            <p className="mt-6 max-w-xl text-white/70 leading-relaxed text-base lg:text-lg">
              Doryium Coin (DORY) is a next-generation digital currency built around a
              simple vision: connecting people, technology, and opportunity through a
              modern blockchain ecosystem on Solana.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#how-to-buy" data-testid="hero-buy-btn">
                <button className="btn-gold rounded-xl px-7 py-3.5 font-heading uppercase tracking-widest text-sm">
                  Buy DORYIUM
                </button>
              </a>
              <a
                href={`https://solscan.io/token/${CONTRACT}`}
                target="_blank"
                rel="noreferrer"
                data-testid="hero-solscan-btn"
              >
                <button className="btn-outline-gold rounded-xl px-7 py-3.5 font-heading uppercase tracking-widest text-sm">
                  View on Solscan
                </button>
              </a>
            </div>

            <div className="mt-8 flex items-center gap-4">
              <SocialIconLink href="https://x.com" label="Twitter" testid="hero-social-x"><Twitter className="w-4 h-4" /></SocialIconLink>
              <SocialIconLink href="https://t.me" label="Telegram" testid="hero-social-tg"><TelegramIcon className="w-4 h-4" /></SocialIconLink>
              <SocialIconLink href="https://doryium.com" label="Website" testid="hero-social-web"><Globe className="w-4 h-4" /></SocialIconLink>
              <SocialIconLink href="https://discord.com" label="Discord" testid="hero-social-dc"><MessageCircle className="w-4 h-4" /></SocialIconLink>
              <div className="ml-4 flex items-center gap-2 text-xs uppercase tracking-widest text-white/50 font-heading">
                Built on
                <span className="text-[#ffd977] font-semibold">SOLANA</span>
              </div>
            </div>
          </div>

          {/* COIN */}
          <div className="relative flex items-center justify-center">
            <div className="relative w-[340px] sm:w-[440px] lg:w-[540px]">
              <div className="coin-halo" />
              <img
                src={COIN_IMG}
                alt="DORYIUM Coin"
                className="w-full coin-float rounded-full"
                data-testid="hero-coin-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 -mt-6 mb-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={<TrendingUp className="w-5 h-5" />} label="Chain" value="Solana" testid="stat-chain" />
          <StatCard icon={<Users className="w-5 h-5" />} label="Community" value={memberCount.toLocaleString()} testid="stat-community" />
          <StatCard icon={<Wallet className="w-5 h-5" />} label="Supply" value="1.0B" testid="stat-supply" />
          <StatCard icon={<ShieldCheck className="w-5 h-5" />} label="Type" value="SPL Token" testid="stat-type" />
        </div>
      </section>

      {/* CONTRACT + ABOUT */}
      <section id="about" className="max-w-7xl mx-auto px-5 lg:px-10 pb-20 grid lg:grid-cols-2 gap-6">
        <div className="panel panel-gold p-6 lg:p-8">
          <div className="eyebrow mb-4">Contract Address</div>
          <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl p-3">
            <code
              className="text-[#ffd977] font-mono text-xs sm:text-sm break-all flex-1"
              data-testid="contract-address"
            >
              {CONTRACT}
            </code>
            <button
              onClick={copyAddress}
              className="btn-gold rounded-lg px-4 py-2 text-xs font-heading uppercase tracking-widest flex items-center gap-2"
              data-testid="copy-contract-btn"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <div className="mt-3 text-xs text-white/50">
            Short: <span className="text-white/80 font-mono">{shortAddr}</span>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <a
              href={`https://solscan.io/token/${CONTRACT}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contract-solscan"
            >
              <button className="btn-outline-gold w-full rounded-lg py-2.5 text-xs font-heading uppercase tracking-widest flex items-center justify-center gap-2">
                View on Solscan <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </a>
            <a
              href={`https://dexscreener.com/solana/${CONTRACT}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contract-dexscreener"
            >
              <button className="btn-outline-gold w-full rounded-lg py-2.5 text-xs font-heading uppercase tracking-widest flex items-center justify-center gap-2">
                View on DexScreener <ExternalLink className="w-3.5 h-3.5" />
              </button>
            </a>
          </div>
        </div>

        <div className="panel p-6 lg:p-8">
          <div className="eyebrow mb-4">About DORYIUM</div>
          <h3 className="font-display text-2xl lg:text-3xl text-white uppercase tracking-wide">
            More Than <span className="gold-text">Just A Coin</span>
          </h3>
          <p className="mt-4 text-white/70 leading-relaxed">
            DORYIUM is designed to be more than a token — it is a movement. Our goal
            is to build a strong global community, create real opportunities for
            holders, and become part of the future of blockchain innovation.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MiniPill icon={<Users className="w-3.5 h-3.5" />} text="Community Driven" />
            <MiniPill icon={<Globe className="w-3.5 h-3.5" />} text="Global Vision" />
            <MiniPill icon={<Sparkles className="w-3.5 h-3.5" />} text="Real Opportunities" />
          </div>
        </div>
      </section>

      <div className="divider-gold max-w-6xl mx-auto" />

      {/* VISION */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="flex flex-col items-start gap-2 mb-10">
          <div className="eyebrow">Our Vision</div>
          <h2 className="font-display text-3xl lg:text-5xl text-white uppercase tracking-wide">
            Building A <span className="gold-text">Brighter Future</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <VisionCard icon={<Users className="w-6 h-6" />} title="Strong Community" desc="Bringing people together worldwide." />
          <VisionCard icon={<Sparkles className="w-6 h-6" />} title="Real Utility" desc="Exploring real-world applications." />
          <VisionCard icon={<TrendingUp className="w-6 h-6" />} title="Growth & Opportunity" desc="Creating value for the future." />
          <VisionCard icon={<Globe className="w-6 h-6" />} title="Global Movement" desc="People • Technology • Opportunity" />
        </div>
      </section>

      {/* TOKENOMICS */}
      <section id="tokenomics" className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="mb-10">
          <div className="eyebrow">Tokenomics</div>
          <h2 className="font-display text-3xl lg:text-5xl text-white uppercase tracking-wide">
            <span className="gold-text">DORY</span> By The Numbers
          </h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <TokenCard label="Total Supply" value="1,000,000,000" hint="1 Billion DORY" />
          <TokenCard label="Blockchain" value="Solana" hint="Fast • Low fees" />
          <TokenCard label="Token Type" value="SPL" hint="Solana Program Library" />
          <TokenCard label="Community Allocation" value="60%" hint="Holders & rewards" />
          <TokenCard label="Liquidity" value="25%" hint="Locked & fair" />
          <TokenCard label="Team & Growth" value="15%" hint="Development & marketing" />
        </div>
      </section>

      {/* HOW TO BUY */}
      <section id="how-to-buy" className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="mb-10">
          <div className="eyebrow">How To Buy</div>
          <h2 className="font-display text-3xl lg:text-5xl text-white uppercase tracking-wide">
            Get Your <span className="gold-text">DORYIUM</span> In 4 Steps
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StepCard n="01" title="Create a Wallet" desc="Install Phantom or Solflare. Any Solana-compatible wallet works." />
          <StepCard n="02" title="Fund with SOL" desc="Buy SOL on Coinbase or your favorite exchange and send it to your wallet." />
          <StepCard n="03" title="Copy Contract" desc="Copy the DORYIUM contract address above and open Jupiter." />
          <StepCard n="04" title="Swap for DORY" desc="Paste the address on Jupiter, choose your amount, confirm the swap." />
        </div>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={`https://jup.ag/swap/SOL-${CONTRACT}`} target="_blank" rel="noreferrer" data-testid="buy-jupiter-btn">
            <button className="btn-gold rounded-xl px-7 py-3.5 font-heading uppercase tracking-widest text-sm">
              Swap on Jupiter
            </button>
          </a>
          <button
            onClick={copyAddress}
            className="btn-outline-gold rounded-xl px-7 py-3.5 font-heading uppercase tracking-widest text-sm flex items-center gap-2"
            data-testid="buy-copy-btn"
          >
            <Copy className="w-4 h-4" /> Copy Contract Address
          </button>
        </div>
      </section>

      {/* EXCHANGES / APPS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="mb-10">
          <div className="eyebrow">Trackers & Exchanges</div>
          <h2 className="font-display text-3xl lg:text-5xl text-white uppercase tracking-wide">
            Find <span className="gold-text">DORY</span> Everywhere
          </h2>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {EXCHANGES.map((ex) => (
            <a
              key={ex.name}
              href={ex.url}
              target="_blank"
              rel="noreferrer"
              className="panel panel-gold p-5 hover:-translate-y-1 hover:gold-glow transition-all"
              data-testid={ex.testid}
            >
              <div className="text-xs uppercase tracking-widest text-[#f5c65a] font-heading">{ex.tag}</div>
              <div className="mt-2 font-display text-lg text-white">{ex.name}</div>
              <div className="mt-4 flex items-center text-xs text-white/50">
                Visit <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* ROADMAP */}
      <section id="roadmap" className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="mb-10">
          <div className="eyebrow">Roadmap</div>
          <h2 className="font-display text-3xl lg:text-5xl text-white uppercase tracking-wide">
            The <span className="gold-text">Journey</span> Ahead
          </h2>
        </div>
        <div className="relative">
          <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#f5c65a]/60 via-[#f5c65a]/20 to-transparent" />
          <div className="space-y-8">
            <RoadmapItem side="left" phase="Phase 1 — Launch" items={["Token deployment on Solana", "Community formation", "Website & branding go live"]} />
            <RoadmapItem side="right" phase="Phase 2 — Growth" items={["Listings on trackers (Solscan, DexScreener)", "Jupiter swap integration", "Community campaigns"]} />
            <RoadmapItem side="left" phase="Phase 3 — Expansion" items={["Merch store launch", "Partnerships & collabs", "Exchange listings"]} />
            <RoadmapItem side="right" phase="Phase 4 — Future" items={["Utility use-cases", "Ecosystem grants", "A global DORYIUM movement"]} />
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section id="community" className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="panel panel-gold p-8">
            <div className="eyebrow mb-4">Join the Community</div>
            <h2 className="font-display text-3xl lg:text-4xl text-white uppercase">
              Register & <span className="gold-text">Belong</span>
            </h2>
            <p className="mt-3 text-white/60">
              Sign up to get updates, airdrop info and priority access to the DORYIUM ecosystem.
            </p>
            <form onSubmit={handleJoin} className="mt-6 space-y-4" data-testid="community-form">
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-widest font-heading">Name</Label>
                <input
                  className="doryium-input mt-2"
                  placeholder="Satoshi N."
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="community-name-input"
                />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-widest font-heading">Email</Label>
                <input
                  className="doryium-input mt-2"
                  type="email"
                  placeholder="you@doryium.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="community-email-input"
                />
              </div>
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-widest font-heading">Solana Wallet (optional)</Label>
                <input
                  className="doryium-input mt-2"
                  placeholder="Your SOL address"
                  value={form.wallet}
                  onChange={(e) => setForm({ ...form, wallet: e.target.value })}
                  data-testid="community-wallet-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading.join}
                className="btn-gold rounded-xl w-full py-3.5 font-heading uppercase tracking-widest text-sm disabled:opacity-70"
                data-testid="community-submit-btn"
              >
                {loading.join ? "Joining..." : "Join the Movement"}
              </button>
            </form>
          </div>

          <div className="space-y-6">
            <div className="panel p-6">
              <div className="eyebrow mb-4">Connect With Us</div>
              <div className="grid grid-cols-2 gap-3">
                <SocialTile href="https://x.com" name="Twitter / X" icon={<Twitter className="w-4 h-4" />} testid="community-x" />
                <SocialTile href="https://t.me" name="Telegram" icon={<TelegramIcon className="w-4 h-4" />} testid="community-tg" />
                <SocialTile href="https://discord.com" name="Discord" icon={<MessageCircle className="w-4 h-4" />} testid="community-dc" />
                <SocialTile href="https://doryium.com" name="Website" icon={<Globe className="w-4 h-4" />} testid="community-web" />
              </div>
            </div>

            <div className="panel panel-gold p-6" id="merch">
              <div className="flex items-center gap-3 mb-3">
                <ShoppingBag className="w-5 h-5 text-[#f5c65a]" />
                <div className="eyebrow" style={{ margin: 0 }}>Merch Coming Soon</div>
              </div>
              <h3 className="font-display text-2xl text-white uppercase">
                Wear The <span className="gold-text">Vision</span>
              </h3>
              <p className="text-white/60 mt-2 text-sm">
                Get notified when the first drop of official DORYIUM merch goes live.
              </p>
              <form onSubmit={handleMerch} className="mt-4 flex gap-2" data-testid="merch-form">
                <input
                  className="doryium-input flex-1"
                  type="email"
                  placeholder="your@email.com"
                  value={merchEmail}
                  onChange={(e) => setMerchEmail(e.target.value)}
                  data-testid="merch-email-input"
                />
                <button
                  type="submit"
                  disabled={loading.merch}
                  className="btn-gold rounded-lg px-5 text-xs font-heading uppercase tracking-widest disabled:opacity-70"
                  data-testid="merch-submit-btn"
                >
                  Notify Me
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="max-w-7xl mx-auto px-5 lg:px-10 py-20">
        <div className="panel p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="eyebrow mb-4">Partnerships & Press</div>
              <h2 className="font-display text-3xl lg:text-4xl text-white uppercase">
                {"Let's Build"} <span className="gold-text">Together</span>
              </h2>
              <p className="mt-4 text-white/60">
                {"Have a proposal, want to collab on merch, or press an interview? Send a message and we'll respond."}
              </p>
              <div className="mt-6 flex items-center gap-4">
                <Rocket className="w-5 h-5 text-[#f5c65a]" />
                <span className="text-white/70">People • Technology • Opportunity</span>
              </div>
            </div>
            <form onSubmit={handleContact} className="space-y-4" data-testid="contact-form">
              <input
                className="doryium-input"
                placeholder="Your Name"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                data-testid="contact-name-input"
              />
              <input
                className="doryium-input"
                type="email"
                placeholder="Your Email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                data-testid="contact-email-input"
              />
              <textarea
                className="doryium-input min-h-[120px] resize-y"
                placeholder="Message"
                value={contactForm.message}
                onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                data-testid="contact-message-input"
              />
              <button
                type="submit"
                disabled={loading.contact}
                className="btn-gold rounded-xl w-full py-3 font-heading uppercase tracking-widest text-sm disabled:opacity-70"
                data-testid="contact-submit-btn"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src={COIN_IMG} alt="DORYIUM" className="w-8 h-8 rounded-full ring-1 ring-[#f5c65a]/60" />
            <span className="font-display gold-text tracking-widest">DORYIUM</span>
            <span className="text-white/40 text-xs ml-2">© {new Date().getFullYear()}</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-white/50">
            <span>Solana SPL Token</span>
            <span>•</span>
            <a href={`https://solscan.io/token/${CONTRACT}`} target="_blank" rel="noreferrer" className="hover:text-[#ffd977]">Solscan</a>
            <span>•</span>
            <a href="/admin" className="hover:text-[#ffd977]" data-testid="footer-admin-link">Admin</a>
          </div>
        </div>
        <div className="mt-6 text-center text-[10px] text-white/30 max-w-3xl mx-auto px-5">
          Cryptocurrency involves risk. DORYIUM is a community-driven token. Nothing on this
          website constitutes financial advice. Do your own research.
        </div>
      </footer>
    </div>
  );
}

/* -------- Small components -------- */

function StatCard({ icon, label, value, testid }) {
  return (
    <div className="panel p-4 flex items-center gap-4" data-testid={testid}>
      <div className="w-10 h-10 rounded-lg bg-[#f5c65a]/15 text-[#ffd977] grid place-items-center">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-heading">{label}</div>
        <div className="font-display text-lg text-white">{value}</div>
      </div>
    </div>
  );
}

function MiniPill({ icon, text }) {
  return (
    <div className="flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs text-white/80">
      <span className="text-[#f5c65a]">{icon}</span>
      {text}
    </div>
  );
}

function VisionCard({ icon, title, desc }) {
  return (
    <div className="panel p-6 hover:panel-gold transition-all hover:-translate-y-1">
      <div className="w-11 h-11 rounded-xl bg-[#f5c65a]/15 text-[#ffd977] grid place-items-center">{icon}</div>
      <div className="mt-4 font-display uppercase text-white text-base tracking-wider">{title}</div>
      <div className="mt-1 text-sm text-white/60">{desc}</div>
    </div>
  );
}

function TokenCard({ label, value, hint }) {
  return (
    <div className="panel p-6">
      <div className="text-xs uppercase tracking-widest text-white/50 font-heading">{label}</div>
      <div className="mt-2 font-display text-2xl gold-text">{value}</div>
      <div className="mt-1 text-xs text-white/50">{hint}</div>
    </div>
  );
}

function StepCard({ n, title, desc }) {
  return (
    <div className="panel p-6 relative overflow-hidden">
      <div className="font-display text-5xl gold-text opacity-30 absolute -top-2 right-3">{n}</div>
      <div className="text-xs uppercase tracking-widest text-[#f5c65a] font-heading">Step {n}</div>
      <div className="mt-2 font-display uppercase text-white tracking-wider">{title}</div>
      <div className="mt-2 text-sm text-white/60">{desc}</div>
    </div>
  );
}

function RoadmapItem({ side, phase, items }) {
  const isLeft = side === "left";
  return (
    <div className="relative lg:grid lg:grid-cols-2 lg:gap-8 items-start">
      <div className={`${isLeft ? "lg:pr-10 lg:text-right" : "lg:col-start-2 lg:pl-10"} pl-12 lg:pl-0`}>
        <div className="panel panel-gold p-6 inline-block max-w-xl">
          <div className="text-xs uppercase tracking-widest text-[#f5c65a] font-heading">{phase.split(" — ")[0]}</div>
          <div className="mt-1 font-display text-white text-lg uppercase">{phase.split(" — ")[1]}</div>
          <ul className={`mt-3 space-y-1 text-sm text-white/70 ${isLeft ? "lg:text-right" : ""}`}>
            {items.map((it) => (<li key={it}>• {it}</li>))}
          </ul>
        </div>
      </div>
      <div className="absolute left-4 lg:left-1/2 top-4 -translate-x-1/2">
        <div className="dot-gold" />
      </div>
    </div>
  );
}

function SocialIconLink({ href, label, testid, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="w-10 h-10 rounded-full border border-white/10 hover:border-[#f5c65a]/70 bg-white/[0.03] grid place-items-center text-white/80 hover:text-[#ffd977] transition-all"
      data-testid={testid}
    >
      {children}
    </a>
  );
}

function SocialTile({ href, name, icon, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#f5c65a]/60 transition-all hover:-translate-y-0.5"
      data-testid={testid}
    >
      <div className="w-8 h-8 grid place-items-center rounded-lg bg-[#f5c65a]/15 text-[#ffd977]">{icon}</div>
      <div className="text-sm text-white/80">{name}</div>
      <ArrowRight className="w-3.5 h-3.5 ml-auto text-white/40" />
    </a>
  );
}
